// ── Inbox — Supabase realtime subscription + poll fallback ────
//
// Owns the lifecycle of the inbox's live-message pipeline:
//   1. The shared live feed of `messages` INSERTs (messages/message-feed.js,
//      primary, instant); the Inbox is one of its listeners.
//   2. setInterval HTTP poll as a safety net for when realtime is down.
//   3. Health tracking — flips a degraded-sync banner and logs to the
//      admin system_events feed when the channel disconnects for >30s.
//
// All long-lived handles (`_feedOff`, `_pollInterval`, `_rt*`) live in
// the factory closure so the parent module doesn't have to thread them.
// Parent state that the realtime module needs to *react to* (active
// conversation, message list, conversations list) is read via getter
// callbacks so the factory always sees fresh references — important
// because the parent reassigns these arrays during cleanup + reload.
//
// Tab-sleep handling: backgrounded tabs lose their WebSocket on browser
// idle timeout. A visibilitychange listener resets the healthy-timestamp
// when the tab returns so reported downtime only counts visible-tab
// time, and the 30s degraded-state timer skips logging when the tab is
// hidden. Both prevent the system_events feed from filling with noise.

import { getSupabase } from '../config.js';
import { POLL_MS } from './format-helpers.js';
import { mergeUnsent } from '../unsent-message.js';
import * as convStore from '../messages/conversation-store.js';
import * as feed from '../messages/message-feed.js';
import { hold, send } from '../messages/channel-registry.js';

/**
 * Build an inbox-realtime controller bound to the parent inbox module's
 * state. The returned object exposes the four methods the parent calls
 * directly plus one cleanup method.
 *
 * @param {object} deps
 * @param {() => string | null} deps.getActiveClientId
 * @param {() => string | null} deps.getActiveGroupId
 * @param {() => 'direct' | 'group' | null} deps.getActiveConvType
 * @param {() => any[]} deps.getActiveMessages — array ref, mutated in place
 * @param {() => void} deps.applyFilter — rebuilds the visible list from the open tab
 * @param {(msg: any) => void} deps.resolveReplyToInPlace
 * @param {() => void} deps.renderChatMessages
 * @param {() => void} deps.renderConversationsList
 *
 * The conversation list, its unread counts and the number of people waiting
 * are the shared store's (messages/conversation-store.js): a live row goes
 * through convStore.applyIncoming, a read through convStore.markRead.
 */
export function createInboxRealtime(deps) {
  const {
    getActiveClientId,
    getActiveGroupId,
    getActiveConvType,
    getActiveMessages,
    getActiveGroupMembers,
    pulseTypingIndicator,
    applyFilter,
    resolveReplyToInPlace,
    renderChatMessages,
    renderConversationsList,
  } = deps;

  // The Inbox's hold on the shared live feed + health-tracking state.
  let _feedOff = null;
  let _rtState = 'init'; // 'init' | 'connected' | 'degraded' | 'disconnected'
  let _rtLastHealthy = 0;
  let _rtErrorCount = 0;
  let _rtVisibilityWired = false;
  let _rtErrorWindowStart = 0;
  let _rtDegradedTimer = null;
  let _rtErrorWasLogged = false;

  // Poll fallback state — one interval handle reused for both direct
  // and group polls; switching conversations stops the old interval
  // before starting the new one.
  let _pollInterval = null;
  let _pollInFlight = false;

  // Reset the healthy-timestamp whenever the tab comes back into the
  // foreground. Fixes the "realtime_reconnect with downtime=2h" log noise
  // that fired every time a backgrounded tab was reopened — the 2h wasn't
  // real downtime, just tab idle time.
  function wireRealtimeVisibilityOnce() {
    if (_rtVisibilityWired || typeof document === 'undefined') return;
    _rtVisibilityWired = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        _rtLastHealthy = Date.now();
        _rtErrorWasLogged = false;
        readTheOpenConversationOnReturn();
      }
    });
    globalThis.window?.addEventListener?.('focus', readTheOpenConversationOnReturn);
  }

  /** The coach is looking at the Inbox page: the tab in front, the page showing. */
  function viewingInbox() {
    const view = document.getElementById('view-inbox');
    return document.visibilityState === 'visible' && !!view && !view.classList.contains('hidden');
  }

  /** The open conversation's key, or null. */
  function openKey() {
    if (getActiveConvType() === 'group' && getActiveGroupId()) return `g:${getActiveGroupId()}`;
    if (getActiveConvType() === 'direct' && getActiveClientId()) return `c:${getActiveClientId()}`;
    return null;
  }

  /** @param {string} key */
  const waiting = (key) => { const c = convStore.find(key); return !!c && ((c.unreadCount || 0) > 0 || !!c.manuallyUnread); };

  // The coach is back on the tab (or it took focus) with a conversation open on
  // the Inbox page: what arrived while they were away is read now, and only now.
  function readTheOpenConversationOnReturn() {
    const key = openKey();
    if (!key || !viewingInbox() || !waiting(key)) return;
    convStore.markRead(key);
    renderConversationsList();
  }

  function startMessagePoll(clientId) {
    stopMessagePoll();
    _pollInterval = setInterval(async () => {
      if (!getActiveClientId()) { stopMessagePoll(); return; }
      if (_pollInFlight) return;
      _pollInFlight = true;
      try {
        const supabase = getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { stopMessagePoll(); return; }
        // Only mark as read when tab is visible AND coach is on the inbox view
        const isViewingInbox = viewingInbox();
        const res = await fetch('/.netlify/functions/messages-list', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ clientId, skipMarkRead: !isViewingInbox }),
        });
        if (!res.ok) return;
        const json = await res.json();
        const incoming = json.messages || [];
        let changed = false;
        // Update read_at + reply_to + reactions on existing messages.
        // Realtime pushes raw DB rows (no reply_to/reactions resolved), so
        // the poll is what hydrates them the first time.
        const activeMessages = getActiveMessages();
        const existingMap = new Map(activeMessages.map(m => [m.id, m]));
        for (const m of incoming) {
          const existing = existingMap.get(m.id);
          if (!existing) continue;
          if (!existing.read_at && m.read_at) {
            existing.read_at = m.read_at;
            changed = true;
          }
          // delivered→sent never downgrades: a poll snapshot read just
          // before the mark-delivered commit must not undo the live receipt.
          if (
            m.delivery_status &&
            existing.delivery_status !== m.delivery_status &&
            !(existing.delivery_status === 'delivered' && m.delivery_status === 'sent')
          ) {
            existing.delivery_status = m.delivery_status;
            changed = true;
          }
          if (!existing.reply_to && m.reply_to) {
            existing.reply_to = m.reply_to;
            changed = true;
          }
          if (JSON.stringify(existing.reactions || []) !== JSON.stringify(m.reactions || [])) {
            existing.reactions = m.reactions || [];
            changed = true;
          }
        }
        // A message unsent while the live "unsent" event was missed (asleep
        // tab, dropped socket) turns into the unsent line here. Before this
        // the check ignored the flag and the words stayed until a reload.
        if (mergeUnsent(activeMessages, incoming)) changed = true;
        const existingIds = new Set(activeMessages.map(m => m.id));
        const newMsgs = incoming.filter(m => !existingIds.has(m.id));
        if (newMsgs.length > 0) {
          activeMessages.push(...newMsgs);
          changed = true;
          // Read only while the coach is looking; otherwise it keeps counting (readTheOpenConversationOnReturn).
          if (isViewingInbox && waiting(`c:${clientId}`)) { convStore.markRead(`c:${clientId}`); renderConversationsList(); }
        }
        if (changed) renderChatMessages();
      } catch {} finally { _pollInFlight = false; }
    }, POLL_MS);
  }

  function stopMessagePoll() {
    if (_pollInterval) { clearInterval(_pollInterval); _pollInterval = null; }
  }

  function startGroupMessagePoll(groupId) {
    stopMessagePoll();
    _pollInterval = setInterval(async () => {
      if (!getActiveGroupId() || getActiveConvType() !== 'group') { stopMessagePoll(); return; }
      if (_pollInFlight) return;
      _pollInFlight = true;
      try {
        const supabase = getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { stopMessagePoll(); return; }
        const isViewingInbox = viewingInbox();
        const res = await fetch('/.netlify/functions/messages-group-list', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ groupId, skipMarkRead: !isViewingInbox }),
        });
        if (!res.ok) return;
        const json = await res.json();
        const incoming = json.messages || [];
        const activeMessages = getActiveMessages();
        // Merge member read-cursors (drives "Read by N/M") — fallback for
        // the realtime group-read broadcast.
        let changed = false;
        const liveMembers = getActiveGroupMembers?.() || [];
        for (const srv of (json.members || [])) {
          const member = liveMembers.find(m => m.clientId === srv.clientId);
          if (member && srv.lastReadAt && (!member.lastReadAt || member.lastReadAt < srv.lastReadAt)) {
            member.lastReadAt = srv.lastReadAt;
            changed = true;
          }
        }
        const existingMap = new Map(activeMessages.map(m => [m.id, m]));
        for (const m of incoming) {
          const existing = existingMap.get(m.id);
          if (!existing) continue;
          if (JSON.stringify(existing.reactions || []) !== JSON.stringify(m.reactions || [])) {
            existing.reactions = m.reactions || [];
            changed = true;
          }
        }
        const existingIds = new Set(activeMessages.map(m => m.id));
        const newMsgs = incoming.filter(m => !existingIds.has(m.id));
        if (newMsgs.length > 0) {
          activeMessages.push(...newMsgs);
          changed = true;
          if (isViewingInbox && waiting(`g:${groupId}`)) { convStore.markRead(`g:${groupId}`); renderConversationsList(); }
        }
        if (changed) renderChatMessages();
      } catch {} finally { _pollInFlight = false; }
    }, POLL_MS);
  }

  // ── Group channel ────────────────────────────────────────────
  // Held while a GROUP conversation is open (`group-msgs-{groupId}` —
  // members can't share a 1:1 topic), through the shared channel registry
  // (messages/channel-registry.js). Carries reactions (server-emitted
  // from messages-react), member typing, and read-cursor bumps for the
  // live "Read by N/M" label. New group MESSAGES already arrive via the
  // shared live feed. The coach's own echoes are ignored.
  let _groupRelease = null;
  let _groupChannelId = null;

  function subscribeGroupChannel(groupId) {
    // ⚠️ THE SAME GROUP KEEPS ITS CHANNEL (2026-10-01). Opening a group that
    // was already open used to close its channel and ask for the same topic
    // straight back. supabase.channel(topic) returns a channel still
    // registered under that topic, and the one just closed stays registered
    // until the server confirms the close, so the group got its closing
    // channel back and subscribe() did nothing: no live reactions, typing or
    // "Read by" until another conversation was opened. The open hold is kept
    // (and the registry keeps a let-go channel through a grace anyway).
    if (_groupRelease && _groupChannelId === groupId) return;
    unsubscribeGroupChannel();
    if (!groupId) return;
    _groupChannelId = groupId;
    _groupRelease = hold({ groupId }, {
      reaction: (payload) => {
        if (!payload?.messageId || !payload?.emoji || payload?.who === 'coach') return;
        if (getActiveConvType() !== 'group' || getActiveGroupId() !== groupId) return;
        const msg = getActiveMessages().find(m => m.id === payload.messageId);
        if (!msg) return;
        const op = payload.op === 'removed' ? 'removed' : 'added';
        const reactions = [...(msg.reactions || [])];
        const idx = reactions.findIndex(r => r.emoji === payload.emoji);
        if (idx >= 0) {
          const next = { ...reactions[idx], count: Math.max(0, reactions[idx].count + (op === 'added' ? 1 : -1)) };
          if (next.count === 0) reactions.splice(idx, 1);
          else reactions[idx] = next;
        } else if (op === 'added') {
          reactions.push({ emoji: payload.emoji, count: 1, mine: false });
        }
        msg.reactions = reactions;
        renderChatMessages();
      },
      typing: (payload) => {
        if (payload?.who !== 'client') return;
        if (getActiveConvType() !== 'group' || getActiveGroupId() !== groupId) return;
        pulseTypingIndicator?.();
      },
      'group-read': (payload) => {
        if (payload?.who !== 'client' || !payload?.clientId || !payload?.at) return;
        if (getActiveConvType() !== 'group' || getActiveGroupId() !== groupId) return;
        const member = (getActiveGroupMembers?.() || []).find(m => m.clientId === payload.clientId);
        if (!member) return;
        if (!member.lastReadAt || member.lastReadAt < payload.at) {
          member.lastReadAt = payload.at;
          renderChatMessages(); // "Read by N/M" moves live
        }
      },
    });
  }

  function unsubscribeGroupChannel() {
    // Let go of the hold; the registry removes the channel (removeChannel,
    // after a grace) once no screen holds it.
    if (_groupRelease) { _groupRelease(); _groupRelease = null; _groupChannelId = null; }
  }

  // Coach typing → group members' apps show "Coach is typing…".
  function broadcastGroupTyping() {
    if (!_groupChannelId) return;
    send({ groupId: _groupChannelId }, 'typing', { who: 'coach', name: 'Coach', clientId: null });
  }

  // A message arrived in the conversation open on the Inbox page.
  //
  // The coach is LOOKING at it: the client's message is read right away (the
  // endpoint also broadcasts the Seen receipt), through the shared store, so
  // every number drops with it. Before this, an arrival into the OPEN
  // conversation waited for the next 90s poll tick: the client sat on
  // "Delivered" and the server kept a stale unread count that resurfaced as a
  // phantom badge when switching conversations.
  //
  // ⚠️ NOT LOOKING (the tab behind another one, or another page): it keeps
  // counting. It was zeroed here, so the tab title's number (D7) never showed
  // the open chat's new message; it is read when the coach comes back
  // (readTheOpenConversationOnReturn).
  function readTheOpenConversation(key, msg) {
    if (msg.sender_role === 'client' && viewingInbox()) convStore.markRead(key);
  }

  // A new message row of a client conversation, from the shared live feed
  // (messages/message-feed.js). The feed has already dropped other accounts'
  // rows and Lead Engine rows, applied the row to the shared conversation list
  // (the unread count, the preview, the number of people waiting) and posted
  // the client's Delivered receipt.
  function onMessageRow(msg) {
    const activeMessages = getActiveMessages();
    const activeClientId = getActiveClientId();
    const activeGroupId = getActiveGroupId();
    const activeConvType = getActiveConvType();

    if (msg.group_id) {
      if (activeConvType === 'group' && msg.group_id === activeGroupId) {
        if (!activeMessages.some(m => m.id === msg.id)) {
          resolveReplyToInPlace(msg);
          activeMessages.push(msg);
          renderChatMessages();
        }
        readTheOpenConversation(`g:${msg.group_id}`, msg);
      }
    } else if (msg.client_id === activeClientId && activeConvType === 'direct') {
      if (!activeMessages.some(m => m.id === msg.id)) {
        resolveReplyToInPlace(msg);
        activeMessages.push(msg);
        renderChatMessages();
      }
      readTheOpenConversation(`c:${msg.client_id}`, msg);
    }

    // ⚠️ THROUGH THE OPEN TAB, NOT AROUND IT. This set the visible list to
    // EVERY loaded chat: a live message filled the Archived tab with active
    // chats, and All with archived ones once the Archived tab had loaded them.
    applyFilter();
    renderConversationsList();
  }

  function initInboxRealtime() {
    if (_feedOff) return;
    if (!getSupabase()) return;
    // The one database listener is the shared feed's; the Inbox listens to it.
    const offRow = feed.subscribe(onMessageRow);
    const offStatus = feed.onStatus(onRealtimeStatus);
    _feedOff = () => { offRow(); offStatus(); };
    feed.start();
    // Wire the visibility listener once (idempotent). Keeps downtime
    // measurements bound to tab-visible time so backgrounded-tab
    // reconnects don't pollute the health log.
    wireRealtimeVisibilityOnce();
  }

  // ── Realtime health tracking + degraded banner ──────────────
  // Status comes from Supabase's channel.subscribe callback:
  //   SUBSCRIBED  — healthy, reset trackers
  //   CHANNEL_ERROR / CLOSED / TIMED_OUT — unhealthy
  // If unhealthy persists >30s, show the degraded banner and log an
  // admin event. Recovers silently once SUBSCRIBED returns.
  function onRealtimeStatus(status, err) {
    const now = Date.now();
    if (status === 'SUBSCRIBED') {
      // Only log the recovery event if the error was actually logged
      // (not a tab-sleep artifact) AND downtime is meaningfully long
      // (>= 5s) — sub-5s bounces are transient and pollute the log.
      if (_rtState === 'degraded' && _rtErrorWasLogged) {
        const downtime = _rtLastHealthy ? now - _rtLastHealthy : null;
        if (downtime == null || downtime >= 5_000) {
          logSystemEvent('realtime_reconnect', 'info', {
            downtime_ms: downtime,
            platform: 'crm',
          });
        }
      }
      _rtState = 'connected';
      _rtLastHealthy = now;
      _rtErrorCount = 0;
      _rtErrorWasLogged = false;
      if (_rtDegradedTimer) { clearTimeout(_rtDegradedTimer); _rtDegradedTimer = null; }
      updateRealtimeBanner();
      return;
    }

    // Any non-subscribed status is provisionally unhealthy.
    _rtErrorCount++;
    if (now - _rtErrorWindowStart > 60_000) {
      _rtErrorWindowStart = now;
      _rtErrorCount = 1;
    }

    // Mark degraded in state; banner + log waits 30s to absorb transient
    // reconnects (supabase auto-backs-off) and also to skip browser
    // tab-visibility disconnects that auto-recover quickly.
    if (_rtState !== 'degraded' && _rtState !== 'disconnected') {
      _rtState = 'degraded';
      if (_rtDegradedTimer) clearTimeout(_rtDegradedTimer);
      _rtDegradedTimer = setTimeout(() => {
        if (_rtState !== 'degraded') return;
        // Skip if the browser tab is currently hidden — that's expected
        // disconnect behavior on page visibility change, not an outage.
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          return;
        }
        _rtErrorWasLogged = true;
        updateRealtimeBanner();
        logSystemEvent('realtime_error', _rtErrorCount >= 3 ? 'error' : 'warning', {
          status,
          error: err?.message || null,
          error_count: _rtErrorCount,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          platform: 'crm',
        });
      }, 30_000);
    }
  }

  function updateRealtimeBanner() {
    let el = document.getElementById('inbox-realtime-banner');
    const isDegraded = _rtState === 'degraded' || _rtState === 'disconnected';
    if (!isDegraded) {
      if (el) el.remove();
      return;
    }
    if (el) return; // already showing
    el = document.createElement('div');
    el.id = 'inbox-realtime-banner';
    el.className = 'inbox-realtime-banner';
    el.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <span>Live sync paused — falling back to polling</span>
    `;
    const host = document.getElementById('inbox-chat-messages')?.parentElement
      || document.getElementById('view-inbox');
    host?.prepend(el);
  }

  // Fire-and-forget event logger. Service-side rate-limits to 1/30s per
  // (coach, client, event_type) tuple — this helper doesn't need its own
  // rate-limit, just won't await the response.
  async function logSystemEvent(eventType, severity, detail) {
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      fetch('/.netlify/functions/system-event-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ eventType, severity, detail }),
      }).catch(() => {});
    } catch {}
  }

  function unsubscribeRealtime() {
    if (_feedOff) { _feedOff(); _feedOff = null; }
    feed.stop();
  }

  return {
    initInboxRealtime,
    startMessagePoll,
    stopMessagePoll,
    startGroupMessagePoll,
    subscribeGroupChannel,
    unsubscribeGroupChannel,
    broadcastGroupTyping,
    unsubscribeRealtime,
  };
}
